import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigationType } from 'react-router-dom';
import { useWakeLock } from 'react-screen-wake-lock';
import { DeviceConnectionError, LavvaBluetooth } from 'lavva.webbluetooth/build/js/LavvaBluetooth';
import { uniqBy } from 'lodash';
import * as Sentry from '@sentry/react';
import { SelectOptionType } from '../../../../../components/dialog/helper';
import { ONLY_LABEL_OPTION_VALUE } from '../../../../../const';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import { LoggingInfo, LogLevel } from '../../../../../data-access/gql-types/graphql';
import { environment } from '../../../../../environment';
import { ROUTES } from '../../../../../routes/routes';
import { ChildrenProps } from '../../../../../types';
import { isLocalApp, isWisniowski } from '../../../../../utils/helpers';
import { getSignalStrength } from '../../../../../utils/helpers/network';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import * as storage from '../../../storage';
import { NetworkState } from '../components/network-state';
import { useBTLogs } from '../hooks/use-bt-logs';
import { useConnectErrors } from '../hooks/use-connect-errors';
import { useNativeWebBluetooth } from '../hooks/use-native-web-bluetooth';
import LavvaDevicesSupported from '../supported/Lavva.json';
import WisniowskiDevicesSupported from '../supported/Wisniowski.json';
import {
  AddingDeviceEnablePayload,
  AdvancedSettings,
  BluetoothData,
  BluetoothMethodRequest,
  BluetoothMethodResponse,
  DiagnosticPayload,
  RegisterLavvaProgressPayload,
  RegisterLavvaRequestBody,
  RegisterLavvaResponsePayload,
  RemoveWifiPayload,
  SetWifiPayload,
  SettingsPayload,
  UpdateByWifiErrorPayload,
  UpdateByWifiOkPayload,
  UpdateByWifiSuccessPayload,
  UpdateData,
  WiFiKnowsNetworksPayload,
  WiFiNetworksPayload,
  SessionIdPayload,
} from '../types';
import { BluetoothProviderState, GateTypePrefix } from '../types';
import {
  checkIfAddingDeviceEnableMessageActive,
  deviceTypeIcon,
  initialBluetoothDataState,
  initialContextState,
  CHARACTERISTIC_UUID,
  SERVICE_UUID,
  prepareSupportedDevicesFilters,
} from '../utils';

export const BluetoothContext = createContext<BluetoothProviderState>(initialContextState);

export const useBluetoothContext = (): BluetoothProviderState => useContext(BluetoothContext);

export const BluetoothContextProvider: React.FC<ChildrenProps> = ({ children }) => {
  const bluetoothData = useRef<BluetoothData>(initialBluetoothDataState);
  const sessionIdData = useRef<SessionIdPayload | null>(null);
  const btCharacteristic = useRef<any>(null);
  const btResult = useRef<BluetoothDevice | null>(null);
  const btLogLevel = useRef<LogLevel>(LogLevel.None);
  const deviceNameBT = useRef<string>('');
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const { t } = useTranslation('device-add');
  const { t: tc } = useTranslation('common');
  const { selectedInstallationId } = useInstallationContext();
  const {
    percentage,
    isCustomBluetooth,
    foundDevices,
    dialogScanning,
    scanning,
    setDialogScanning,
    setFoundDevices,
    handleDeviceConnect,
    stoppedScanning,
    setScanning,
    WebBluetooth,
  } = useNativeWebBluetooth();
  const [deviceConnected, setDeviceConnected] = useState<boolean>(false);
  const [redoRegisteringDisabled, setRedoRegisteringDisabled] = useState<boolean>(false);
  const [checkList, updateCheckList] = useState<string[]>([]);
  const [registerErrorMessage, setRegisterErrorMessage] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [identifing, setIdentifing] = useState<boolean>(false);
  const [typeOptions, setTypeOptions] = useState<SelectOptionType<string>[]>([]);
  const [back, setBack] = useState<number>(0);
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettings | null>(null);
  const [isAlreadyAdded, setIsAlreadyAdded] = useState<boolean>(false);
  const [wifiList, setWifiList] = useState<SelectOptionType<string>[]>([]);
  const [wifiKnowsList, setWifiKnowsList] = useState<SelectOptionType<string>[] | null>(null);
  const [wifiLoading, setWifiLoading] = useState<boolean>(false);
  const [updatePayload, setUpdatePayload] = useState<UpdateData>(initialContextState.updatePayload);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { handleConnectErrors } = useConnectErrors();
  const { showAndSendLog } = useBTLogs();
  const { request, release } = useWakeLock();

  useEffect(() => {
    request();
  }, []);

  useEffect(() => {
    if (navType === 'PUSH') {
      setBack((prev) => prev + 1);
    } else if (navType === 'POP') {
      setBack((prev) => Math.max(0, prev - 1));
    }
  }, [pathname]);

  const updateBluetoothData = (obj: Record<string, string | string[] | boolean | DiagnosticPayload | null>) => {
    bluetoothData.current = { ...bluetoothData.current, ...obj };
  };

  const handleNotifications = (event) => {
    const value = event.target.value;
    const messages: string[] = [];

    for (let i = 0; i < value.byteLength; i++) {
      messages.push('0x' + ('00' + value.getUint8(i).toString(16)).slice(-2));
    }

    showAndSendLog({
      loggingInfo: LoggingInfo.None,
      messages,
      btLogLevel: btLogLevel.current,
      macAddress: bluetoothData.current.mac,
      sessionData: sessionIdData.current,
    });
  };

  const resetBluetoothData = () => {
    if (btCharacteristic.current) {
      btCharacteristic.current.removeEventListener('characteristicvaluechanged', handleNotifications);
    }
    btResult.current = null;
    sessionIdData.current = null;
    bluetoothData.current = initialBluetoothDataState;
    deviceNameBT.current = initialContextState.deviceNameBT;
    setUpdatePayload(initialContextState.updatePayload);
    setRedoRegisteringDisabled(false);
    setDeviceConnected(false);
    setIdentifing(false);
    setWifiList([]);
    setWifiKnowsList([]);
    setTypeOptions([]);
    setAdvancedSettings(null);
    setBack(0);
    turnOffBackdrop();
  };

  const returnToStartPage = () => {
    if (pathname !== ROUTES.DeviceAddBluetooth() && back) {
      navigate(-back);
      setBack(0);
    }
  };

  const checkIfConnected = async () => {
    if (!deviceConnected) {
      resetBluetoothData();
      await disconnect();

      returnToStartPage();
    }
  };

  const onTimeoutError = () => {
    toastError({ content: tc('errors.somethingWentWrong') });
    turnOffBackdrop();
  };

  const clearTimeoutError = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };

  const setTimeoutError = () => {
    clearTimeoutError();
    timeout.current = setTimeout(onTimeoutError, 1000 * 15);
  };

  const onDisconnected = () => {
    setRedoRegisteringDisabled(false);
    setDeviceConnected(false);

    showAndSendLog({
      loggingInfo: LoggingInfo.Disconnected,
      messages: [],
      btLogLevel: btLogLevel.current,
      macAddress: bluetoothData.current.mac,
      sessionData: sessionIdData.current,
    });

    turnOffBackdrop();
    console.log('Rozłączono z urządzeniem');
  };

  const handleMessage = async (message: string) => {
    console.log('MESSAGE', message);

    if (message) {
      clearTimeoutError();

      try {
        const data = JSON.parse(message);

        switch (data.method) {
          case BluetoothMethodResponse.Settings: {
            stoppedScanning();
            setDialogScanning(false);
            setFoundDevices([]);
            const payload = data.payload as SettingsPayload;
            console.log('SETTINGS', data.payload);

            setTypeOptions(
              payload.types.map((type) => {
                return {
                  value: type,
                  label: t(`deviceTypes.${type.toUpperCase()}`),
                  icon: deviceTypeIcon[type],
                };
              }),
            );

            if (payload.advancedSettings) {
              setAdvancedSettings(payload.advancedSettings);
            }

            setIsAlreadyAdded(!!payload.isAlreadyAdded);

            updateBluetoothData({
              deviceName: payload.name,
              type: payload.currentType,
              mac: payload.mac,
              identifyIsAvailable: !!payload.identifyIsAvailable,
              isAlreadyAdded: !!payload.isAlreadyAdded,
              diagnostic: payload.diagnostic || null,
            });

            console.log(`Odebrano: Settings (${JSON.stringify(data)})`);
            handleNext();
            break;
          }
          case BluetoothMethodResponse.SessionId: {
            const payload = data.payload as SessionIdPayload;
            console.log('SESSION_ID', data.payload);

            sessionIdData.current = payload;

            addBTLogHandler(btResult.current as BluetoothDevice);

            break;
          }
          case BluetoothMethodResponse.WiFiNetworks: {
            const payload = data.payload as WiFiNetworksPayload;

            const wifi = uniqBy(payload.wifiList, 'SSID').map((wifi) => {
              let supportedAuth = true;
              if (wifi.supportedAuth !== undefined) supportedAuth = wifi.supportedAuth;

              return {
                label: wifi.SSID,
                value: wifi.SSID,
                intValue: wifi.RSSI ? getSignalStrength(parseInt(wifi.RSSI)) : 0,
                supportedAuth,
              };
            });

            const knowsList = [...(wifiKnowsList || [])];

            const list = wifi.map((x) => {
              const foundInKnows = knowsList.find((y) => y.value === x.value);

              if (foundInKnows) {
                return {
                  label: x.label,
                  value: x.value,
                  weight: foundInKnows.weight,
                  icon: <NetworkState signal={x.intValue} remembered />,
                  ...(!x.supportedAuth && {
                    subLabel: t('bluetooth.networkUnsupported'),
                    disabled: true,
                  }),
                };
              }

              return {
                label: x.label,
                value: x.value,
                icon: <NetworkState signal={x.intValue} />,
                ...(!x.supportedAuth && { subLabel: t('bluetooth.networkUnsupported'), disabled: true }),
              };
            });

            const knows = list.filter((wifiItem) => knowsList.find((knows) => knows.value === wifiItem.value));
            const rest = list.filter((wifiItem) => !knowsList.find((knows) => knows.value === wifiItem.value));

            setWifiKnowsList(knows);
            setWifiList(rest);

            console.log(`Odebrano: WiFiNetworks (${JSON.stringify(data)})`);
            setWifiLoading(false);
            break;
          }
          case BluetoothMethodResponse.KnownWifis: {
            const payload = data.payload as WiFiKnowsNetworksPayload;

            const wifi = payload.wifiList.map((wifi) => ({
              label: wifi,
              value: wifi,
              weight: 1,
            }));
            console.log('KNOWNS WIFI', wifi);
            setWifiKnowsList(wifi);

            console.log(`Odebrano: WiFiKnowsNetworks (${JSON.stringify(data)})`);

            break;
          }
          case BluetoothMethodResponse.RegisterLavvaResponse: {
            turnOffBackdrop();
            setRedoRegisteringDisabled(false);
            const payload = data.payload as RegisterLavvaResponsePayload;

            if (payload.status) {
              console.log(`Sukces: RegisterLavvaResponse (${JSON.stringify(data)})`);

              if (bluetoothData.current.ssid && bluetoothData.current.password) {
                storage.setItem(
                  'bluetoothNetwork',
                  JSON.stringify({ ssid: bluetoothData.current.ssid, password: `!${bluetoothData.current.password}` }),
                );
              }

              await sendSuccessConfirmation();
              if (!deviceNameBT.current.includes(GateTypePrefix.SmartAWSC)) {
                navigate(ROUTES.DeviceAddBluetoothSuccess(payload.deviceId));
              } else {
                navigate(ROUTES.DeviceAddBluetoothWisniowskiSuccess());
              }
            } else {
              setRegisterErrorMessage(
                payload.errorCode ? t(`bluetooth.errors.${payload.errorCode}`) : tc('errors.somethingWentWrong'),
              );
              if (payload.errorCode) {
                setErrorCode(payload.errorCode);

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { password, ...rest } = bluetoothData.current;

                if (!isLocalApp) {
                  Sentry.captureMessage('REGISTER DEVICE LAVVA', {
                    level: 'info',
                    extra: {
                      status: payload.status,
                      errorCode: payload.errorCode,
                      bluetoothData: {
                        ...rest,
                      },
                    },
                  });
                }
              }

              console.log(`Error: RegisterLavvaResponse (${JSON.stringify(data)})`);
            }

            break;
          }
          case BluetoothMethodResponse.RegisterLavvaProgress: {
            const payload = data.payload as RegisterLavvaProgressPayload;
            console.log('REGISTER DEVICE RESPONSE', payload);

            if (payload.step) {
              turnOffBackdrop();
              updateCheckList((prevList) => [...prevList, t(`bluetooth.progress.${payload.step}`)]);
            }

            break;
          }
          case BluetoothMethodResponse.SetWifiResponse: {
            const payload = data.payload as SetWifiPayload;
            turnOffBackdrop();
            console.log('SET WIFI RESPONSE', payload);

            if (payload.status) {
              if (bluetoothData.current.ssid && bluetoothData.current.password) {
                storage.setItem(
                  'bluetoothNetwork',
                  JSON.stringify({ ssid: bluetoothData.current.ssid, password: `!${bluetoothData.current.password}` }),
                );
              }

              await sendSuccessConfirmation();
              navigate(ROUTES.DeviceAddBluetoothSetWifiSuccess());
            } else {
              toastError({ content: t('bluetooth.setWifiError') });
            }

            break;
          }
          case BluetoothMethodResponse.RemoveWifiResponse: {
            const payload = data.payload as RemoveWifiPayload;
            turnOffBackdrop();

            if (payload.status) {
              toastSuccess({ content: t('bluetooth.wifiRemoved') });
              getKnowsWifiNetworks();
            } else {
              toastError({ content: tc('errors.somethingWentWrong') });
            }

            break;
          }
          case BluetoothMethodResponse.UpdateByWifiResponse: {
            const payload = data.payload as
              | UpdateByWifiOkPayload
              | UpdateByWifiErrorPayload
              | UpdateByWifiSuccessPayload;

            if ((payload as UpdateByWifiOkPayload).update) {
              setUpdatePayload({ ...(payload as UpdateByWifiOkPayload).update, status: null });
            } else if ((payload as UpdateByWifiErrorPayload).errorCode) {
              setUpdatePayload((prev) => ({
                ...prev,
                status: (payload as UpdateByWifiErrorPayload).status,
                errorCode: (payload as UpdateByWifiErrorPayload).errorCode,
              }));
            } else {
              if ((payload as UpdateByWifiSuccessPayload).status) {
                setUpdatePayload((prev) => ({ ...prev, status: (payload as UpdateByWifiSuccessPayload).status }));
                await sendSuccessConfirmation();
              }
            }

            break;
          }
          case BluetoothMethodResponse.AddingDeviceEnable: {
            const addingDeviceActive = checkIfAddingDeviceEnableMessageActive(deviceNameBT.current);
            if (!addingDeviceActive) return;

            const payload = data.payload as AddingDeviceEnablePayload;

            if (payload.status) {
              navigate(ROUTES.DeviceAddBluetoothIdentify());
            } else {
              navigate(ROUTES.DeviceAddBluetoothWisniowskiStart(), { replace: true });
            }

            break;
          }
          default: {
            break;
          }
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
        turnOffBackdrop();

        toastError({ content: tc('errors.downloadData') });

        Sentry.captureMessage('BLUETOOTH PARSE ERROR', {
          level: 'info',
          extra: {
            message,
            error,
          },
        });
      }
    }
  };

  const terminal = useMemo(() => {
    const bt = new LavvaBluetooth();
    bt.OnDisconnectedEvent.Subscribe(onDisconnected);
    bt.OnCharacteristicValueChangedEvent.Subscribe(handleMessage);
    const filters = prepareSupportedDevicesFilters(isWisniowski ? WisniowskiDevicesSupported : LavvaDevicesSupported);
    bt.SetFilter(filters);

    return bt;
  }, []);

  const disconnect = async () => {
    turnOffBackdrop();
    setDeviceConnected(false);

    const device = terminal.GetConnectedDevice();
    if (device) {
      turnOnBackdrop();
      const disconnectResult = await terminal.DisconnectDeviceAsync(device);
      turnOffBackdrop();
      return disconnectResult;
    }
  };

  const identifyDevice = async () => {
    try {
      turnOnBackdrop();

      await terminal.SendDataAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.Identify,
          payload: {},
        }),
      );

      console.log('Identify: zakończono pomyślnie');
      setIdentifing(true);
      turnOffBackdrop();
    } catch (error) {
      toastError({ content: t('bluetooth.identifyError') });
      console.log(`Identify error: ${error}`);
      turnOffBackdrop();
    }
  };

  const updateWifiData = (ssid: string, password: string) => {
    bluetoothData.current = { ...bluetoothData.current, ssid, password };
  };

  const getDeviceSettings = async () => {
    try {
      setTimeoutError();
      turnOnBackdrop();

      await terminal.SendDataAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.GetSettings,
          payload: {},
        }),
      );

      console.log('Get Settings: zakończono pomyślnie');

      if (btLogLevel.current !== LogLevel.None && btResult.current) {
        getSessionId();
      }
    } catch (error) {
      setDeviceConnected(false);
      toastError({ content: t('bluetooth.settingsError') });
      console.log(`Get Settings error: ${error}`);
      setDialogScanning(false);
      turnOffBackdrop();
    }
  };

  const getSessionId = async () => {
    try {
      await terminal.SendDataAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.GetSessionId,
          payload: {},
        }),
      );

      console.log('Get SessionId: zakończono pomyślnie');
    } catch (error) {
      console.log(`Get SessionId error: ${error}`);
    }
  };

  const updateByWifi = async () => {
    try {
      setUpdatePayload(initialContextState.updatePayload);

      const data = {
        method: BluetoothMethodRequest.UpdateByWifi,
        payload: {
          wifi: {
            ssid: bluetoothData.current.ssid,
            password: bluetoothData.current.password,
          },
        },
      };

      await terminal.SendDataAsync(JSON.stringify(data));
      console.log('Update Device: zakończono pomyślnie');
    } catch (error) {
      console.log(`Update Device error: ${error}`);
    }
  };

  const setWifi = async (ssid: string, password: string) => {
    try {
      turnOnBackdrop();

      const data = {
        method: BluetoothMethodRequest.SetWifi,
        payload: {
          wifi: {
            ssid,
            password,
          },
        },
      };

      console.log('SET WIFI BODY', data);

      await terminal.SendDataAsync(JSON.stringify(data));

      console.log('Set Wifi: zakończono pomyślnie');
    } catch (error) {
      console.log(`Set Wifi error: ${error}`);
      turnOffBackdrop();
    }
  };

  const removeWifi = async (ssid: string) => {
    try {
      turnOnBackdrop();

      const data = {
        method: BluetoothMethodRequest.RemoveWifi,
        payload: {
          ssid,
        },
      };

      await terminal.SendDataAsync(JSON.stringify(data));

      console.log('Remove Wifi: zakończono pomyślnie');
    } catch (error) {
      console.log(`Remove Wifi error: ${error}`);
      toastError({ content: t('bluetooth.forgettingWifiError') });
      turnOffBackdrop();
    }
  };

  const getWifiNetworks = async () => {
    try {
      setWifiLoading(true);
      console.log('Get WiFi Networks: wysłano');

      await terminal.SendAndWaitForResponseAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.GetWiFiNetworks,
          payload: {},
        }),
        10000,
      );

      console.log('Get WiFi Networks: zakończono pomyślnie');
    } catch (error) {
      toastError({ content: t('bluetooth.wifiError') });
      console.log(`Get WiFi Networks error: ${error}`);
      setWifiLoading(false);
    }
  };

  const getKnowsWifiNetworks = async () => {
    try {
      setWifiKnowsList([]);
      setWifiList([]);
      setWifiLoading(true);

      if (!bluetoothData.current.diagnostic) {
        getWifiNetworks();
        return;
      }

      console.log('Get WiFi Knows Networks: wysłano');

      await terminal.SendAndWaitForResponseAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.GetKnownWifis,
          payload: {},
        }),
        10000,
      );

      console.log('Get WiFi Knows Networks: zakończono pomyślnie');
      getWifiNetworks();
    } catch (error) {
      toastError({ content: t('bluetooth.wifiKnowsError') });
      console.log(`Get WiFi Knows Networks error: ${error}`);
      setWifiLoading(false);
    }
  };

  const onSelectedDevice = () => {
    setDeviceConnected(true);
    getDeviceSettings();
  };

  const onConnectError = (error) => {
    setDeviceConnected(false);
    console.log('CONNECT ERROR', error);
    setDialogScanning(false);
    turnOffBackdrop();
  };

  const sendNotSupportedLog = (macAddress: string) => {
    showAndSendLog({
      loggingInfo: LoggingInfo.NotSupported,
      messages: [],
      btLogLevel: btLogLevel.current,
      macAddress: macAddress,
      sessionData: sessionIdData.current,
    });
  };

  const addBTLogHandler = async (result: BluetoothDevice) => {
    const macAddress = bluetoothData.current.mac;

    showAndSendLog({
      loggingInfo: LoggingInfo.Connected,
      messages: [],
      btLogLevel: btLogLevel.current,
      macAddress,
      sessionData: sessionIdData.current,
    });

    try {
      const server = await (result as BluetoothDevice).gatt?.connect();
      if (!server) {
        sendNotSupportedLog(macAddress);
        return;
      }

      const service = await server.getPrimaryService(SERVICE_UUID);
      if (!service) {
        sendNotSupportedLog(macAddress);
        return;
      }

      const characteristic = await service.getCharacteristic(CHARACTERISTIC_UUID);
      if (!characteristic) {
        sendNotSupportedLog(macAddress);
        return;
      }

      btCharacteristic.current = characteristic;

      await btCharacteristic.current.startNotifications();

      showAndSendLog({
        loggingInfo: LoggingInfo.Supported,
        messages: [],
        btLogLevel: btLogLevel.current,
        macAddress: macAddress,
        sessionData: sessionIdData.current,
      });

      console.log('> Notifications started');

      btCharacteristic.current.addEventListener('characteristicvaluechanged', handleNotifications);
    } catch (error) {
      console.log('Characteristic error', error);
      sendNotSupportedLog(macAddress);
    }
  };

  const searchBluetooth = async (custom: boolean) => {
    console.log('SEARCH BLUETOOTH');
    resetBluetoothData();
    await disconnect();
    stoppedScanning();
    setFoundDevices([]);
    setScanning(true);

    if (custom) setDialogScanning(true);
    else {
      turnOnBackdrop();
    }

    console.log('BLUETOOTH CONNECT STARTED');

    try {
      const result = await terminal.RequestDeviceAndConnectAsync();

      if ((result as BluetoothDevice).id) {
        console.log('CONNECT SUCCESS', result);
        btResult.current = result as BluetoothDevice;

        deviceNameBT.current = (result as BluetoothDevice).name || '';
        onSelectedDevice();
      } else {
        onConnectError(result);
        handleConnectErrors(result as DeviceConnectionError);
      }
    } catch (error) {
      onConnectError(error);
    }
  };

  const registerDevice = async (ssid: string, password: string) => {
    try {
      turnOnBackdrop();
      setRedoRegisteringDisabled(true);
      updateCheckList([]);
      setRegisterErrorMessage('');
      setErrorCode('');

      const data: RegisterLavvaRequestBody = {
        method: BluetoothMethodRequest.RegisterLavva,
        payload: {
          wifi: {
            ssid,
            password,
          },
          name: bluetoothData.current.name,
          type: bluetoothData.current.type,
          installationId: selectedInstallationId || '',
          discoveryUrl:
            window.location.hostname === 'localhost' ? 'https://discovery.dev2.lavva.cloud' : environment.DISCOVERY_URL,
          ...(bluetoothData.current.subTypes.length && {
            settings: {
              subTypes: bluetoothData.current.subTypes,
            },
          }),
        },
      };

      await terminal.SendDataAsync(JSON.stringify(data));

      console.log('Register Lavva: zakończono pomyślnie');
    } catch (error) {
      console.log(`Register Lavva error: ${error}`);
    }
  };

  const sendSuccessConfirmation = async () => {
    try {
      await terminal.SendDataAsync(
        JSON.stringify({
          method: BluetoothMethodRequest.SendSuccessConfirmation,
          payload: {},
        }),
      );

      console.log('SendSuccessConfirmation: zakończono pomyślnie');
    } catch (error) {
      console.log(`Get Settings error: ${error}`);
    }
  };

  const handleNext = async () => {
    turnOffBackdrop();
    console.log('BT device name', deviceNameBT.current);

    if (
      deviceNameBT.current.includes(GateTypePrefix.SmartAWSC) ||
      deviceNameBT.current.includes(GateTypePrefix.SmartAwGate)
    ) {
      navigate(ROUTES.DeviceAddBluetoothWisniowskiStart());
    } else {
      navigate(ROUTES.DeviceAddBluetoothIdentify());
    }
  };

  const releaseLock = () => release();

  const setBtLogLevel = (level: LogLevel) => {
    console.log(`> Level: `, level);
    btLogLevel.current = level;
  };

  const values: BluetoothProviderState = {
    deviceNameBT: deviceNameBT.current,
    bluetoothData: { ...bluetoothData.current },
    deviceConnected,
    identifing,
    wifiList: [
      ...(wifiKnowsList && wifiKnowsList.length
        ? [{ label: t('bluetooth.myNetworks'), value: ONLY_LABEL_OPTION_VALUE, disabled: true }]
        : []),
      ...[...(wifiKnowsList || [])],
      ...(wifiList.length
        ? [{ label: t('bluetooth.otherNetworks'), value: ONLY_LABEL_OPTION_VALUE, disabled: true }]
        : []),
      ...wifiList,
    ],
    typeOptions,
    wifiLoading,
    isAlreadyAdded,
    checkList,
    registerErrorMessage,
    updateBluetoothData,
    redoRegisteringDisabled,
    setRedoRegisteringDisabled,
    checkIfConnected,
    searchBluetooth,
    getWifiNetworks,
    getDeviceSettings,
    identifyDevice,
    registerDevice,
    resetBluetoothData,
    releaseLock,
    getKnowsWifiNetworks,
    updateByWifi,
    disconnect,
    updatePayload,
    setWifi,
    removeWifi,
    percentage,
    isCustomBluetooth,
    foundDevices,
    dialogScanning,
    scanning,
    setDialogScanning,
    handleDeviceConnect,
    WebBluetooth,
    updateWifiData,
    returnToStartPage,
    errorCode,
    advancedSettings,
    setBtLogLevel,
  };

  return <BluetoothContext.Provider value={values}>{children}</BluetoothContext.Provider>;
};
